import { CartCoupon } from "../cartCoupon";
import { useShopAvailability } from "../shopAvailability";
import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { LuShoppingBag, LuX, LuArrowRight } from "react-icons/lu";
import { CartContent } from "../../pages/cart";
import { CartResumeContent } from "../cartResume";
import { useCartAmounts } from "../../hooks/useCartAmounts";
import { formatMoney } from "../../functions/money";
import { checkoutLogin } from "../../pages/login/returnTarget";
import { setComponentActive } from "../../reducers/userConfigSlice";
import "./style.css";

import { CartDrawerContext as Context } from "./context";
export { useCartDrawer } from "./context";

const DrawerOrder = ({ quote }) => {
  const { canOrder, message } = useShopAvailability();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const logged = useSelector(state => state.aplicationConfig.userConfig.logged);
  const lines = useSelector(state => state.aplicationConfig.userConfig.cart?.ShoppingCartProducts || []);
  const blocked = !canOrder || quote.busy || quote.error || !quote.amounts || !lines.length || lines.some(line => line.unavailable);
  return <footer className="eats-drawer-footer">
    {!canOrder && <p role="status">{message || "Comprobando horario de pedidos…"}</p>}
    <button type="button" className="eats-drawer-continue" aria-label={logged ? "Continuar con el pedido" : "Identifícate para continuar"} disabled={blocked} onClick={() => { dispatch(setComponentActive("checkout")); navigate(logged ? "/checkout" : checkoutLogin); }}>
      Continuar{quote.busy ? " · Calculando…" : !quote.error && quote.amounts ? ` · ${formatMoney(quote.amounts.total)}` : ""}<LuArrowRight aria-hidden="true" />
    </button>
  </footer>;
};

const DrawerItems = ({ addedLine }) => {
  const quote = useCartAmounts();
  const body = useRef(null);
  useEffect(() => {
    if (!addedLine) return;
    const frame = window.requestAnimationFrame(() => {
      const container = body.current;
      const key = JSON.stringify([addedLine.productId, addedLine.variantId || "", [...(addedLine.itemIds || [])].sort(), addedLine.note || ""]);
      const row = [...(container?.querySelectorAll("[data-cart-line-key]") || [])].find(element => element.dataset.cartLineKey === key);
      if (row) container.scrollTo({ top: container.scrollTop + row.getBoundingClientRect().top - container.getBoundingClientRect().top, behavior: "smooth" });
    });
    return () => window.cancelAnimationFrame(frame);
  }, [addedLine]);
  return <><div ref={body} className="eats-drawer-body">
    <h3 className="eats-drawer-section-title">Productos</h3>
    <CartContent drawer canMakeOrder={false} quote={quote} />
    <CartCoupon />
    <section className="eats-drawer-summary" aria-label="Resumen del pedido">
      <h3 className="eats-drawer-section-title">Resumen del pedido</h3>
      <div className="eats-drawer-summary-card">
        {quote.busy && <p role="status">Calculando el importe…</p>}
        {quote.error && <div role="alert">No se pudo calcular el importe. <button type="button" onClick={quote.retry}>Reintentar</button></div>}
        <div className="eats-drawer-total"><strong>Total</strong><strong>{!quote.busy && !quote.error && quote.amounts ? formatMoney(quote.amounts.total) : "—"}</strong></div>
        <p className="eats-drawer-tax-note">IVA incluido</p>
        <details><summary>Ver desglose</summary><CartResumeContent showCoupon={false} canMakeOrder={false} localized quote={quote} showOrderTotals={false} /></details>
      </div>
    </section>
  </div><DrawerOrder quote={quote} /></>;
};

export const CartDrawerProvider = ({ children }) => {
  const [open, setOpen] = useState(false);
  const [addedLine, setAddedLine] = useState(null);
  const dialog = useRef(null);
  const trigger = useRef(null);
  const location = useLocation();
  const navigate = useNavigate();
  const currentPath = useRef(location.pathname);
  const triggerPath = useRef(location.pathname);
  currentPath.current = location.pathname;
  const products = useSelector(state => state.aplicationConfig.userConfig.cart?.ShoppingCartProducts || []);
  const openCart = useCallback(line => { setAddedLine(line?.productId ? line : null); trigger.current = document.activeElement; triggerPath.current = currentPath.current; setOpen(true); }, []);
  const closeCart = useCallback(() => setOpen(false), []);
  useEffect(() => { setAddedLine(null); setOpen(location.state?.openCartOnReturn === true); }, [location.key, location.state?.openCartOnReturn]);
  useEffect(() => {
    const element = dialog.current;
    if (!open) { if (element?.open) element.close(); return; }
    element.showModal();
    const html = document.documentElement;
    const previous = html.style.getPropertyValue("overflow-y");
    const priority = html.style.getPropertyPriority("overflow-y");
    html.style.setProperty("overflow-y", "hidden", "important");
    element.querySelector("button")?.focus();
    return () => {
      if (element.open) element.close();
      if (previous) html.style.setProperty("overflow-y", previous, priority); else html.style.removeProperty("overflow-y");
      if (currentPath.current === triggerPath.current && trigger.current?.isConnected) trigger.current.focus({ preventScroll: true });
    };
  }, [open]);
  const quantity = products.reduce((sum, line) => sum + Number(line.quantity || 0), 0);
  return <Context.Provider value={{ openCart, closeCart }}>
    {children}
    <dialog ref={dialog} className="eats-cart-drawer" aria-labelledby="cart-drawer-title" onCancel={event => { event.preventDefault(); closeCart(); }} onClose={closeCart}
      onPointerDown={event => { const rect = event.currentTarget.getBoundingClientRect(); if (event.target === event.currentTarget && (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom)) closeCart(); }}>
      {open && <>
        <header className="eats-drawer-heading"><div><h2 id="cart-drawer-title">Tu pedido</h2>{quantity > 0 && <p>{quantity} {quantity === 1 ? "producto" : "productos"}</p>}</div><button type="button" aria-label="Cerrar tu pedido" onClick={closeCart}><LuX aria-hidden="true" /></button></header>
        {products.length ? <DrawerItems addedLine={addedLine} /> : <div className="eats-drawer-empty">
          <span className="eats-drawer-empty-icon"><LuShoppingBag aria-hidden="true" /></span>
          <h3>Tu carrito está vacío</h3><p>Añade productos del catálogo para empezar.</p>
          <button type="button" className="eats-drawer-continue" onClick={() => { closeCart(); if (location.pathname !== "/") navigate("/"); }}>Ver catálogo</button>
        </div>}
      </>}
    </dialog>
  </Context.Provider>;
};
